import React, { Component } from "react";
import axios from "axios";
import { customAlphabet } from "nanoid";
import { withRouter } from "react-router-dom";
import baseurl from "./baseurl";
import SignaturePad from "./SignaturePad";
import ToolTipIcon from "../imgs/tooltip.svg";
import "../styles/Form4.css";

const STEP_TITLES = [
  "Financial Agreement",
  "Patient Demographics & Insurance",
  "Medical Consent",
  "Medical History",
  "Medical History (continued)",
];

const RACE_OPTIONS = [
  "American Indian or Alaska Native",
  "Asian",
  "Native Hawaiian or Other Pacific Islander",
  "Black or African American",
  "White or Caucasian",
  "Hispanic or Latino",
  "Other Race",
  "Declines",
];

const HOW_HEARD_OPTIONS = [
  "Physician Referral",
  "Advertisement",
  "Website",
  "Health Fair",
  "Insurance Company",
  "Family/Friend",
];

const MEDICAL_CONDITIONS_PLAIN = [
  "AIDS/HIV positive",
  "Anemia",
  "Arthritis",
  "Asthma",
  "Barrett's Esophagus",
  "Celiac Disease",
  "Chemical Dependency",
  "Colon Polyps",
  "COPD/Emphysema",
  "Crohn's Disease",
  "Defibrillator/ICD",
  "Depression",
  "Diverticulosis/Diverticulitis",
  "Epilepsy/Seizure Disorder",
  "GERD",
  "Gout",
  "Heart Attack",
  "Heart Disease",
  "Herpes",
  "High Blood Pressure",
  "Irritable Bowel Syndrome",
  "Kidney Disease",
  "Liver Disease",
  "Migraine Headaches",
  "Multiple Sclerosis",
  "Pacemaker",
  "Pancreatitis",
  "Prostate Problems",
  "Psychiatric Care",
  "Rheumatic Fever",
  "Rheumatoid Arthritis",
  "Sleep Apnea",
  "Stroke",
  "Thyroid Disease",
  "Tuberculosis",
  "Sexually Transmitted Disease",
  "Ulcers",
  "Ulcerative Colitis",
];

const FAMILY_HISTORY_ITEMS = [
  { key: "colonCancer", label: "Colon Cancer" },
  { key: "cancerFamily", label: "Cancer", hasType: true },
  { key: "celiacDisease", label: "Celiac Disease" },
  { key: "crohnsUlcerativeColitis", label: "Crohn's/Ulcerative Colitis" },
  { key: "diabetesFamily", label: "Diabetes" },
  { key: "heartDiseaseFamily", label: "Heart Disease" },
  { key: "liverGallbladderDisease", label: "Liver and/or Gallbladder Disease" },
  { key: "pancreaticDisease", label: "Pancreatic Disease" },
];

const SYMPTOM_GROUPS = {
  General: ["Difficulty Sleeping", "Fever", "Weight Loss"],
  Allergic: ["Allergies to food", "Seasonal Allergies"],
  Cardiovascular: [
    "Chest Pain",
    "High Blood Pressure",
    "Irregular Heart Rhythm",
    "Swollen ankles",
  ],
  Dermatology: ["Hives", "Itching", "Rash"],
  Endocrine: ["Diabetes Mellitus", "Thyroid Problems"],
  "Eyes/Ears/Nose/Throat": [
    "Blurred Vision",
    "Mouth Ulcers",
    "Ringing in Ears",
    "Sore Throat",
  ],
  "Hematologic/Lymphatic": ["Anemia", "Bleeding Easily", "Swollen Lymph Nodes"],
  Gastrointestinal: [
    "Abdominal Pain",
    "Bloody Bowel Movements",
    "Constipation",
    "Diarrhea",
    "Difficulty Swallowing",
    "Heartburn",
    "Hemorrhoids",
    "Jaundice",
    "Loss of Bowel Control",
    "Nausea",
    "Using Laxatives",
    "Vomiting",
  ],
  Genitourinary: ["Blood in Urine", "Trouble Urinating"],
  Musculoskeletal: ["Arthritic Pain", "Joint Pain", "Muscle Pain"],
  Neurological: ["Seizures", "Severe Headaches"],
  Psychiatric: ["Anxiety", "Depression"],
  Respiratory: ["Asthma", "Cough", "Shortness of Breath"],
};

const emptyMedRow = () => ({ name: "", dose: "", frequency: "" });
const emptyAllergyRow = () => ({ medicationFood: "", reaction: "" });
const emptyYearRow = () => ({ year: "", detail: "" });

class Form4 extends Component {
  nanoid = customAlphabet("1234567890", 8);

  state = {
    unlocked: false,
    passwordInput: "",
    passwordError: "",
    passwordChecking: false,
    showPassword: false,
    pid: this.nanoid(),
    step: 1,
    maxStepReached: 1,
    submittedSteps: {},
    loading: false,
    errorMessage: "",

    // ---------- Step 1: Financial ----------
    financialInitial: "",
    signaturePage1: "",

    // ---------- Step 2: Patient Demographics & Insurance ----------
    physicianSeeing: "",
    referringPhysician: "",
    primaryCarePhysician: "",
    lastName: "",
    firstName: "",
    mi: "",
    dob: "",
    address: "",
    city: "",
    patientState: "",
    zipCode: "",
    homePhone: "",
    homePhonePrimary: false,
    mobilePhone: "",
    mobilePhonePrimary: false,
    workPhone: "",
    workPhonePrimary: false,
    sex: "",
    maritalStatus: "",
    ssn: "",
    employerName: "",
    occupation: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelationship: "",
    email: "",
    portalConsent: false,
    voicemailHome: "Yes",
    voicemailMobile: "Yes",
    voicemailWork: "Yes",
    selectedRaces: [],
    ethnicity: "",
    preferredLanguage: "",
    translatorRequired: "No",
    pharmacyName: "",
    pharmacyLocation: "",
    pharmacyPhone: "",
    selectedHowHeard: [],

    guarantorIsPatient: true,
    guarantorLastName: "",
    guarantorFirstName: "",
    guarantorMI: "",
    guarantorRelationship: "",
    guarantorAddress: "",
    guarantorCity: "",
    guarantorState: "",
    guarantorZip: "",
    guarantorHomePhone: "",
    guarantorWorkPhone: "",
    guarantorMobilePhone: "",
    guarantorDob: "",
    guarantorSsn: "",
    guarantorSex: "",

    primaryInsuranceCompany: "",
    primaryPolicyID: "",
    primaryGroupNumber: "",
    primarySubscriberName: "",
    primarySubscriberRelationship: "",
    primarySubscriberDob: "",
    secondaryInsuranceCompany: "",
    secondaryPolicyID: "",
    secondaryGroupNumber: "",
    secondarySubscriberName: "",
    secondarySubscriberRelationship: "",
    secondarySubscriberDob: "",

    signaturePage2: "",

    // ---------- Step 3: Medical Consent ----------
    printName: "",
    hasPersonalRepresentatives: false,
    rep1Name: "",
    rep1Relationship: "",
    rep1Phone: "",
    rep2Name: "",
    rep2Relationship: "",
    rep2Phone: "",
    rep3Name: "",
    rep3Relationship: "",
    rep3Phone: "",
    signaturePage3: "",

    // ---------- Step 4: Medical History ----------
    selectedConditions: [],
    otherCondition1: "",
    otherCondition2: "",
    otherCondition3: "",
    otherCondition4: "",
    hasCancerCondition: false,
    cancerType: "",
    hasDiabetes: false,
    diabetesType1: false,
    diabetesType2: false,
    hasHepatitis: false,
    hepatitisA: false,
    hepatitisB: false,
    hepatitisC: false,
    hasHernia: false,
    herniaInguinal: false,
    herniaHiatal: false,

    noMedications: false,
    medications: [emptyMedRow(), emptyMedRow(), emptyMedRow()],
    noKnownAllergies: false,
    allergies: [emptyAllergyRow(), emptyAllergyRow()],
    deniesSurgicalHistory: false,
    surgicalHistory: [emptyYearRow(), emptyYearRow()],
    deniesHospitalization: false,
    hospitalizations: [emptyYearRow(), emptyYearRow()],
    deniesPriorColonoscopy: false,
    lastColonoscopyDate: "",
    hadPolyps: "",
    lastColonoscopyPerformedBy: "",

    signaturePage4: "",

    // ---------- Step 5: Medical History continued ----------
    fatherStatus: "",
    fatherYearOfBirth: "",
    fatherIllness: "",
    motherStatus: "",
    motherYearOfBirth: "",
    motherIllness: "",
    fh_colonCancer_yn: "",
    fh_colonCancer_member: "",
    fh_cancerFamily_yn: "",
    fh_cancerFamily_type: "",
    fh_cancerFamily_member: "",
    fh_celiacDisease_yn: "",
    fh_celiacDisease_member: "",
    fh_crohnsUlcerativeColitis_yn: "",
    fh_crohnsUlcerativeColitis_member: "",
    fh_diabetesFamily_yn: "",
    fh_diabetesFamily_member: "",
    fh_heartDiseaseFamily_yn: "",
    fh_heartDiseaseFamily_member: "",
    fh_liverGallbladderDisease_yn: "",
    fh_liverGallbladderDisease_member: "",
    fh_pancreaticDisease_yn: "",
    fh_pancreaticDisease_member: "",
    numBrothers: "",
    numSisters: "",
    numSons: "",
    numDaughters: "",

    tobaccoUse: "",
    smokerFrequency: "",
    cigsPerDay: "",
    howSoonAfterWaking: "",
    alcoholFrequency: "",
    drinksPerTypicalDay: "",
    sixPlusDrinksFrequency: "",
    hasTattoos: "",
    hasPiercings: "",
    illicitDrugUse: "",
    drinksCaffeine: "",
    caffeineType: "",
    caffeinePerDay: "",
    hadBloodTransfusion: "",
    bloodTransfusionYear: "",
    traveledOutsideUS: "",
    travelDestination: "",
    selectedSymptoms: [],

    signaturePage5: "",
  };

  scrollTop = () => window.scrollTo(0, 0);

  onChange = (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    this.setState({ [e.target.name]: value });
  };

  onPhoneChange = (e) => {
    let input = e.target.value.replace(/\D/g, "");
    if (input.length > 10) input = input.substring(0, 10);
    let formatted = "";
    if (input.length > 6) {
      formatted =
        input.substring(0, 3) +
        "-" +
        input.substring(3, 6) +
        "-" +
        input.substring(6);
    } else if (input.length > 3) {
      formatted = input.substring(0, 3) + "-" + input.substring(3);
    } else {
      formatted = input;
    }
    this.setState({ [e.target.name]: formatted });
  };

  onZipChange = (e) => {
    let input = e.target.value.replace(/[^\d-]/g, "");
    if (input.length > 10) input = input.substring(0, 10);
    const digits = input.replace(/-/g, "");
    if (digits.length <= 5) {
      input = digits;
    } else {
      input = digits.substring(0, 5) + "-" + digits.substring(5, 9);
    }
    this.setState({ [e.target.name]: input });
  };

  isValidPhone = (phone) => /^\d{3}-\d{3}-\d{4}$/.test(phone);
  isValidZip = (zip) => /^\d{5}(-\d{4})?$/.test(zip);
  isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  hasAnyValidPhone = () => {
    const s = this.state;
    return (
      this.isValidPhone(s.homePhone) ||
      this.isValidPhone(s.mobilePhone) ||
      this.isValidPhone(s.workPhone)
    );
  };

  onSignatureChange = (name, value) => {
    this.setState({ [name]: value });
  };

  toggleArrayValue = (arrayKey, value) => {
    this.setState((prev) => {
      const arr = prev[arrayKey];
      const next = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];
      return { [arrayKey]: next };
    });
  };

  addRow = (tableKey, factory) => {
    this.setState((prev) => ({ [tableKey]: [...prev[tableKey], factory()] }));
  };

  removeRow = (tableKey, index) => {
    this.setState((prev) => ({
      [tableKey]: prev[tableKey].filter((_, i) => i !== index),
    }));
  };

  updateRow = (tableKey, index, field, value) => {
    this.setState((prev) => {
      const rows = prev[tableKey].map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );
      return { [tableKey]: rows };
    });
  };

  validateStep = (step) => {
    const s = this.state;
    let hasError = false;

    if (step === 1) {
      if (!s.financialInitial) hasError = true;
      if (!s.signaturePage1) hasError = true;
    }

    if (step === 2) {
      if (!s.firstName || !s.lastName || !s.dob) hasError = true;
      if (!s.address || !s.city || !s.patientState || !s.zipCode)
        hasError = true;
      if (s.zipCode && !this.isValidZip(s.zipCode)) hasError = true;
      if (!this.hasAnyValidPhone()) hasError = true;
      if (!s.email || !this.isValidEmail(s.email)) hasError = true;
      if (!s.sex) hasError = true;
      if (!s.signaturePage2) hasError = true;
    }

    if (step === 3) {
      if (!s.printName) hasError = true;
      if (!s.signaturePage3) hasError = true;
    }

    if (step === 4) {
      if (!s.signaturePage4) hasError = true;
    }

    if (step === 5) {
      if (!s.signaturePage5) hasError = true;
    }

    return hasError;
  };

  componentDidMount() {
    window.addEventListener("popstate", this.onPopState);
  }

  componentWillUnmount() {
    window.removeEventListener("popstate", this.onPopState);
  }

  onPopState = (e) => {
    const step = e.state && e.state.step ? e.state.step : 1;
    this.setState({ step });
    this.scrollTop();
  };

  goToStep = (step) => {
    this.setState({ step });
    window.history.pushState({ step }, "");
    this.scrollTop();
  };

  goToProgressStep = (step) => {
    if (step <= this.state.maxStepReached) {
      this.goToStep(step);
    }
  };

  onPasswordChange = (e) => {
    this.setState({ passwordInput: e.target.value, passwordError: "" });
  };

  toggleShowPassword = () => {
    this.setState((prev) => ({ showPassword: !prev.showPassword }));
  };

  onPasswordSubmit = async (e) => {
    e.preventDefault();
    this.setState({ passwordChecking: true, passwordError: "" });
    try {
      const res = await axios.post(`${baseurl}/form4/verify-access-code`, {
        code: this.state.passwordInput,
      });
      if (res.data.valid) {
        window.history.replaceState({ step: 1 }, "");
        this.setState({
          unlocked: true,
          passwordInput: "",
          passwordError: "",
          passwordChecking: false,
        });
      }
    } catch (err) {
      this.setState({
        passwordChecking: false,
        passwordError:
          err.response?.data?.error || "Incorrect password",
      });
    }
  };

  goNext = () => {
    const { step } = this.state;
    this.setState((prev) => ({
      submittedSteps: { ...prev.submittedSteps, [step]: true },
    }));
    if (this.validateStep(step)) {
      this.scrollTop();
      return;
    }
    if (step < 5) {
      this.setState((prev) => ({
        maxStepReached: Math.max(prev.maxStepReached, step + 1),
      }));
      this.goToStep(step + 1);
    } else {
      this.onSubmit();
    }
  };

  goBack = () => {
    this.goToStep(Math.max(1, this.state.step - 1));
  };

  redirect = () => {
    this.props.history.push("/thank-you");
  };

  handleAlert = () => this.setState({ errorMessage: "" });

  onSubmit = async () => {
    for (let s = 1; s <= 5; s++) {
      if (this.validateStep(s)) {
        this.setState((prev) => ({
          submittedSteps: { ...prev.submittedSteps, [s]: true },
          maxStepReached: Math.max(prev.maxStepReached, s),
          errorMessage:
            "Please complete all required fields before submitting.",
        }));
        this.goToStep(s);
        return;
      }
    }

    this.setState({ loading: true, errorMessage: "" });
    try {
      const payload = { ...this.state, submittedAt: new Date().toISOString() };
      delete payload.loading;
      delete payload.errorMessage;

      const response = await axios.post(
        `${baseurl}/form4/new-patient-registration`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        this.setState({ loading: false });
        this.redirect();
      }
    } catch (err) {
      this.setState({
        loading: false,
        errorMessage: "Something went wrong, please try again later",
      });
    }
  };

  // ============================================================
  // Shared field renderers
  // ============================================================

  field = (name, label, opts = {}) => {
    const { type = "text", required, submitted, onChangeFn, className, ...rest } = opts;
    const value = this.state[name];
    const showError = submitted && required && !value;
    return (
      <div className={`form4-field ${className || ""}`}>
        <label>
          {label}
          {required && <span className="mandate">*</span>}
        </label>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChangeFn || this.onChange}
          className={showError ? "form4-input-error" : ""}
          {...rest}
        />
        {showError && <span className="form4-error">Required</span>}
      </div>
    );
  };

  pillGroup = (name, label, options, required, submitted, plain) => (
    <div className="form4-field form4-field-full">
      <label className={plain ? "" : "form4-question-label"}>
        {label}
        {required && <span className="mandate">*</span>}
      </label>
      <div className="form4-pill-group">
        {options.map((opt) => (
          <label
            className={`form4-pill ${
              this.state[name] === opt ? "form4-pill-selected" : ""
            }`}
            key={opt}
          >
            <input
              type="radio"
              name={name}
              value={opt}
              checked={this.state[name] === opt}
              onChange={this.onChange}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
      {submitted && required && !this.state[name] && (
        <span className="form4-error">Please select an option</span>
      )}
    </div>
  );

  checkboxGroup = (arrayKey, options) => (
    <div className="form4-pill-group">
      {options.map((opt) => (
        <label
          className={`form4-pill ${
            this.state[arrayKey].includes(opt) ? "form4-pill-selected" : ""
          }`}
          key={opt}
        >
          <input
            type="checkbox"
            checked={this.state[arrayKey].includes(opt)}
            onChange={() => this.toggleArrayValue(arrayKey, opt)}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );

  yesNo = (name, label) => (
    <div className="form4-yesno">
      <span className="form4-yesno-label">{label}</span>
      {["Yes", "No"].map((opt) => (
        <label className="form4-yesno-option" key={opt}>
          <input
            type="radio"
            name={name}
            value={opt}
            checked={this.state[name] === opt}
            onChange={this.onChange}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );

  // ============================================================
  // Step 1: Financial
  // ============================================================
  renderStep1() {
    const submitted = !!this.state.submittedSteps[1];
    return (
      <>
        <section className="form4-section">
          <p className="form4-instruction">
            Our office is grateful to have the opportunity to serve you. Our
            primary mission is to provide you with high quality,
            cost-effective compassionate medical care. As we are trying to
            adapt together to the changing ways that healthcare is financed
            and delivered, the following guidelines were developed to help
            you through the process.
          </p>

          <h2 className="form4-section-heading">Payment Guidelines</h2>
          <ul className="form4-static-list">
            <li>
              We collect co-payments, co-insurance, and/or deductibles at the
              time of service unless other written arrangements have been made
              in advance with our office.
            </li>
            <li>
              We accept <strong>Cash, Checks, Money Orders, and Credit Cards</strong>{" "}
              (Visa, Mastercard, American Express and Discover).
            </li>
            <li>
              If your check is returned, a processing fee of $30 will be
              assessed in addition to the amount of the check.
            </li>
            <li>
              A claim will be sent to your insurance company for payment. If
              your insurance company remits the payment to you, please send
              the payment to our office, along with the Explanation of
              Benefits.
            </li>
            <li>
              Any balance that your insurance company determines to be your
              financial responsibility will be billed to you. Payment is due
              in full upon receipt of your statement. Balances that remain
              unpaid after 90 days may be referred to an outside collection
              agency for further collection efforts.
            </li>
          </ul>
          <div className="form4-row">
            {this.field("financialInitial", "Initial to acknowledge 90-day collection policy", {
              required: true,
              submitted,
              maxLength: "4",
              style: { maxWidth: "90px" },
            })}
          </div>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">No Show / Late Cancellations</h2>
          <p className="form4-instruction">
            To provide the best possible service and availability to all
            patients, our practice has implemented the following fees:
          </p>
          <ul className="form4-static-list">
            <li>
              <strong>Office visit</strong> &mdash; We require a 1 business
              day notice for all office visit cancellations. If the required
              notice is not given, a $50.00 charge may be assessed to the
              patient account.
            </li>
            <li>
              <strong>Procedure</strong> &mdash; We require a 3 business day
              notice for all procedure cancellations. If the required notice
              is not given, a $100.00 charge may be assessed to the patient
              account.
            </li>
          </ul>
          <p className="form4-instruction">
            The missed appointment payment may be required prior to, or upon
            the next scheduled procedure or office visit.
          </p>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Ancillary Services</h2>
          <p className="form4-instruction">
            Your physician may refer you to one or more "ancillary services"
            in connection with your medical care. An ancillary service is a
            service supplementing or supporting your medical treatment. The
            following are considered, but not limited to, possible ancillary
            services:
          </p>
          <ul className="form4-static-list form4-static-list-columns">
            <li>Ambulatory Surgery Center</li>
            <li>Infusion Therapy</li>
            <li>Laboratory &amp; Pathology Testing</li>
            <li>Nutritional Services</li>
            <li>Pharmacy Services</li>
            <li>Radiology/Imaging</li>
          </ul>
          <p className="form4-instruction">
            Your physician may have an economic interest in or business
            relationship with the company or person who provides the
            ancillary service(s). You are not obligated to use the provider
            that your physician refers you to. You are free to use any
            provider you choose.
          </p>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Research Programs</h2>
          <p className="form4-instruction">
            Your physician may ask if you would like to participate in a
            clinical trial or research program. These programs may be
            sponsored by a drug company or may be a practice-sponsored
            research program. Your physician may be compensated for services
            rendered in connection with these programs. You are not
            obligated to participate in any research program and your
            permission will be obtained prior to your participation in a
            program that your physician believes may be appropriate for you.
          </p>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">
            When to Present Your Insurance Card
          </h2>
          <p className="form4-instruction">
            Please present your insurance card at <strong>EACH VISIT</strong>.
            Specifically bring to our attention any changes (new card, new
            subscriber or group number, etc.) since your last visit. This
            protects you from paying a bill due to providing incorrect
            information. There is a narrow window (30-45 days) to present an
            accurate claim to the correct insurance company. Failure to do so
            could mean the claim may be denied. If you have a secondary
            insurance, it will be filed as a courtesy. However, if we have
            not received payment from your secondary insurance in a timely
            manner, the balance will become your responsibility.
          </p>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Assignment of Benefits</h2>
          <p className="form4-instruction">
            DHPT may file a claim for services rendered by the physician,
            facility, pathologist and or anesthesia provider. DHPT is
            authorized to transfer any patient overpayment to one of these
            associated entities if applicable. I hereby authorize DHPT to:
          </p>
          <ul className="form4-static-list">
            <li>
              Release any information necessary to the insurance company
              regarding my illness and treatments.
            </li>
            <li>Process claims generated for my examination/treatment.</li>
            <li>
              Allow a photocopy of my signature to be used to process
              insurance claims for a period of a lifetime.
            </li>
            <li>Keep this order in effect until it is revoked by me in writing.</li>
          </ul>
        </section>

        <section className="form4-section">
          <p className="form4-instruction">
            We value you as a patient and we are eager to serve you! Our
            priority is to provide you with the best possible care. If you
            would like to contact our Central Business Office, you may do so
            at 972-408-4410 or 972-408-4400.
          </p>
          <p className="form4-instruction">
            <strong>
              I have read and understand the guidelines and financial
              obligations as stated above.
            </strong>
          </p>
        </section>

        <SignaturePad
          label="Patient Signature"
          name="signaturePage1"
          value={this.state.signaturePage1}
          onChange={this.onSignatureChange}
          error={submitted && !this.state.signaturePage1 ? "Signature is required" : ""}
        />
      </>
    );
  }

  // ============================================================
  // Step 2: Patient Demographics & Insurance
  // ============================================================
  renderStep2() {
    const submitted = !!this.state.submittedSteps[2];
    const s = this.state;
    return (
      <>
        <section className="form4-section form4-section-highlight">
          <h2 className="form4-section-heading">Patient Information</h2>
          <div className="form4-row form4-row-3">
            {this.field("physicianSeeing", "Physician you are seeing today", { submitted })}
            {this.field("referringPhysician", "Referring Physician", { submitted })}
            {this.field("primaryCarePhysician", "Primary Care Physician", { submitted })}
          </div>
          <div className="form4-row form4-row-4">
            {this.field("lastName", "Last Name", { required: true, submitted })}
            {this.field("firstName", "First Name", { required: true, submitted })}
            {this.field("mi", "MI", { submitted, maxLength: "1", className: "form4-field-sm" })}
            {this.field("dob", "Date of Birth", {
              type: "date",
              required: true,
              submitted,
              max: new Date().toISOString().split("T")[0],
            })}
          </div>
          <div className="form4-row form4-row-4">
            {this.field("address", "Address", { required: true, submitted, className: "form4-field-lg" })}
            {this.field("city", "City", { required: true, submitted })}
            {this.field("patientState", "State", { required: true, submitted, maxLength: "2" })}
            {this.field("zipCode", "Zip", {
              required: true,
              submitted,
              onChangeFn: this.onZipChange,
              maxLength: "10",
            })}
          </div>
          <label className="form4-subheading">
            Phone Number<span className="mandate">*</span>
            <span className="tooltip">
              <span className="icon">
                <img
                  src={ToolTipIcon}
                  width="16px"
                  height="16px"
                  alt="At least one phone number is required"
                />
              </span>
              <span className="text">
                Please provide at least one phone number below — home,
                mobile, or work.
              </span>
            </span>
          </label>
          <div className="form4-row form4-row-3">
            <div className="form4-field">
              <label>Home Phone</label>
              <input
                type="tel"
                name="homePhone"
                placeholder="000-000-0000"
                maxLength="12"
                value={s.homePhone}
                onChange={this.onPhoneChange}
                className={
                  submitted && s.homePhone && !this.isValidPhone(s.homePhone)
                    ? "form4-input-error"
                    : ""
                }
              />
              <label className="form4-inline-check">
                <input
                  type="checkbox"
                  name="homePhonePrimary"
                  checked={s.homePhonePrimary}
                  onChange={this.onChange}
                />
                Primary Number
              </label>
              {submitted && s.homePhone && !this.isValidPhone(s.homePhone) && (
                <span className="form4-error">Enter phone as 000-000-0000</span>
              )}
            </div>
            <div className="form4-field">
              <label>Mobile Phone<span className="mandate">*</span></label>
              <input
                type="tel"
                name="mobilePhone"
                placeholder="000-000-0000"
                maxLength="12"
                value={s.mobilePhone}
                onChange={this.onPhoneChange}
                className={
                  submitted && s.mobilePhone && !this.isValidPhone(s.mobilePhone)
                    ? "form4-input-error"
                    : ""
                }
              />
              <label className="form4-inline-check">
                <input
                  type="checkbox"
                  name="mobilePhonePrimary"
                  checked={s.mobilePhonePrimary}
                  onChange={this.onChange}
                />
                Primary Number
              </label>
              {submitted && s.mobilePhone && !this.isValidPhone(s.mobilePhone) && (
                <span className="form4-error">Enter phone as 000-000-0000</span>
              )}
            </div>
            <div className="form4-field">
              <label>Work Phone</label>
              <input
                type="tel"
                name="workPhone"
                placeholder="000-000-0000"
                maxLength="12"
                value={s.workPhone}
                onChange={this.onPhoneChange}
                className={
                  submitted && s.workPhone && !this.isValidPhone(s.workPhone)
                    ? "form4-input-error"
                    : ""
                }
              />
              <label className="form4-inline-check">
                <input
                  type="checkbox"
                  name="workPhonePrimary"
                  checked={s.workPhonePrimary}
                  onChange={this.onChange}
                />
                Primary Number
              </label>
              {submitted && s.workPhone && !this.isValidPhone(s.workPhone) && (
                <span className="form4-error">Enter phone as 000-000-0000</span>
              )}
            </div>
          </div>
          {submitted && !this.hasAnyValidPhone() && (
            <span className="form4-error">
              Please provide at least one valid phone number (home, mobile, or work).
            </span>
          )}

          {this.pillGroup("sex", "Sex", ["Male", "Female", "Transgender"], true, submitted, true)}
          {this.pillGroup(
            "maritalStatus",
            "Marital Status",
            ["Single", "Married", "Divorced", "Separated", "Widowed", "Domestic Partner"],
            false,
            submitted,
            true
          )}

          <div className="form4-row form4-row-3">
            {this.field("ssn", "Social Security Number", { submitted })}
            {this.field("employerName", "Employer Name", { submitted })}
            {this.field("occupation", "Occupation", { submitted })}
          </div>
          <div className="form4-row form4-row-3">
            {this.field("emergencyContactName", "Emergency Contact Name", { submitted })}
            {this.field("emergencyContactPhone", "Emergency Contact Phone Number", {
              submitted,
              onChangeFn: this.onPhoneChange,
              maxLength: "12",
              placeholder: "000-000-0000",
            })}
            {this.field("emergencyContactRelationship", "Emergency Contact Relationship", { submitted })}
          </div>
          <div className="form4-row form4-row-2">
            <div className="form4-field">
              <label>
                Email Address<span className="mandate">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={s.email}
                onChange={this.onChange}
                className={
                  submitted && !this.isValidEmail(s.email) ? "form4-input-error" : ""
                }
              />
              {submitted && !this.isValidEmail(s.email) && (
                <span className="form4-error">Valid email required</span>
              )}
            </div>
            <div className="form4-field">
              <label className="form4-inline-check">
                <input
                  type="checkbox"
                  name="portalConsent"
                  checked={s.portalConsent}
                  onChange={this.onChange}
                />
                I consent for patient portal access
              </label>
            </div>
          </div>

          <div className="form4-row form4-row-3">
            {this.pillGroup("voicemailHome", "Voicemail Messages on Home Phone", ["Yes", "No"], false, submitted, true)}
            {this.pillGroup("voicemailMobile", "Voicemail Messages on Mobile Phone", ["Yes", "No"], false, submitted, true)}
            {this.pillGroup("voicemailWork", "Voicemail Messages on Work Phone", ["Yes", "No"], false, submitted, true)}
          </div>

          <label className="form4-subheading">Which category best describes your race</label>
          {this.checkboxGroup("selectedRaces", RACE_OPTIONS)}

          {this.pillGroup(
            "ethnicity",
            "Ethnicity",
            ["Hispanic or Latino", "Not Hispanic or Latino", "Declines"],
            false,
            submitted,
            true
          )}
          <div className="form4-row form4-row-2">
            {this.field("preferredLanguage", "Preferred Language", { submitted })}
            {this.pillGroup("translatorRequired", "Will a translator be required", ["Yes", "No"], false, submitted, true)}
          </div>
          <div className="form4-row form4-row-3">
            {this.field("pharmacyName", "Pharmacy Name", { submitted })}
            {this.field("pharmacyLocation", "Pharmacy Location", { submitted })}
            {this.field("pharmacyPhone", "Pharmacy Phone Number", {
              submitted,
              onChangeFn: this.onPhoneChange,
              maxLength: "12",
              placeholder: "000-000-0000",
            })}
          </div>

          <label className="form4-subheading">
            How did you hear about Digestive Health Partners of Texas
          </label>
          {this.checkboxGroup("selectedHowHeard", HOW_HEARD_OPTIONS)}
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Responsible Party</h2>
          <p className="form4-instruction">
            Complete this section if the guarantor is someone other than the
            patient or a minor.
          </p>
          <label className="form4-inline-check">
            <input
              type="checkbox"
              checked={s.guarantorIsPatient}
              onChange={() =>
                this.setState((prev) => ({ guarantorIsPatient: !prev.guarantorIsPatient }))
              }
            />
            Guarantor is the patient
          </label>
          {!s.guarantorIsPatient && (
            <div className="form4-subsection">
              <div className="form4-row form4-row-4">
                {this.field("guarantorLastName", "Last Name", { submitted })}
                {this.field("guarantorFirstName", "First Name", { submitted })}
                {this.field("guarantorMI", "MI", { submitted, maxLength: "1", className: "form4-field-sm" })}
                {this.field("guarantorRelationship", "Relationship to Patient", { submitted })}
              </div>
              <div className="form4-row form4-row-4">
                {this.field("guarantorAddress", "Address", { submitted, className: "form4-field-lg" })}
                {this.field("guarantorCity", "City", { submitted })}
                {this.field("guarantorState", "State", { submitted, maxLength: "2" })}
                {this.field("guarantorZip", "Zip", { submitted })}
              </div>
              <div className="form4-row form4-row-3">
                {this.field("guarantorHomePhone", "Home Phone", {
                  submitted,
                  onChangeFn: this.onPhoneChange,
                  maxLength: "12",
                  placeholder: "000-000-0000",
                })}
                {this.field("guarantorWorkPhone", "Work Phone", {
                  submitted,
                  onChangeFn: this.onPhoneChange,
                  maxLength: "12",
                  placeholder: "000-000-0000",
                })}
                {this.field("guarantorMobilePhone", "Mobile Phone", {
                  submitted,
                  onChangeFn: this.onPhoneChange,
                  maxLength: "12",
                  placeholder: "000-000-0000",
                })}
              </div>
              <div className="form4-row form4-row-3">
                {this.field("guarantorDob", "Date of Birth", { type: "date", submitted })}
                {this.field("guarantorSsn", "Social Security Number", { submitted })}
                {this.pillGroup("guarantorSex", "Sex", ["Male", "Female", "Transgender"], false, submitted, true)}
              </div>
            </div>
          )}
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Insurance</h2>
          <p className="form4-instruction">Insurance card(s) must be presented at time of service.</p>
          <div className="form4-row form4-row-3">
            {this.field("primaryInsuranceCompany", "Primary Insurance Company", { submitted })}
            {this.field("primaryPolicyID", "Policy ID Number", { submitted })}
            {this.field("primaryGroupNumber", "Group Number", { submitted })}
          </div>
          <div className="form4-row form4-row-3">
            {this.field("primarySubscriberName", "Subscriber Name", { submitted })}
            {this.field("primarySubscriberRelationship", "Relationship to Patient", { submitted })}
            {this.field("primarySubscriberDob", "Subscriber Date of Birth", { type: "date", submitted })}
          </div>
          <div className="form4-row form4-row-3">
            {this.field("secondaryInsuranceCompany", "Secondary Insurance Company", { submitted })}
            {this.field("secondaryPolicyID", "Policy ID Number", { submitted })}
            {this.field("secondaryGroupNumber", "Group Number", { submitted })}
          </div>
          <div className="form4-row form4-row-3">
            {this.field("secondarySubscriberName", "Subscriber Name", { submitted })}
            {this.field("secondarySubscriberRelationship", "Relationship to Patient", { submitted })}
            {this.field("secondarySubscriberDob", "Subscriber Date of Birth", { type: "date", submitted })}
          </div>
        </section>

        <SignaturePad
          label="Patient Signature"
          name="signaturePage2"
          value={this.state.signaturePage2}
          onChange={this.onSignatureChange}
          error={submitted && !this.state.signaturePage2 ? "Signature is required" : ""}
        />
      </>
    );
  }

  // ============================================================
  // Step 3: Medical Consent
  // ============================================================
  renderStep3() {
    const submitted = !!this.state.submittedSteps[3];
    const s = this.state;
    return (
      <>
        <section className="form4-section">
          <h2 className="form4-section-heading">General Consents</h2>

          <h3>Consent for Medical Treatment</h3>
          <p className="form4-instruction">
            I, the undersigned, as the patient (or the patient's duly
            authorized representative) do hereby voluntarily consent to and
            authorize medical care encompassing all diagnostic and
            therapeutic treatments considered necessary or advisable in the
            judgement of any physician, his assistants, or designees. All
            medical care and treatments will be discussed with me, by the
            physician prior to any proposed treatments, testing, or medical
            procedures being scheduled. I am aware that the practice of
            medicine and surgery is not an exact science. I acknowledge that
            no guarantees have been made to me as to the results of
            treatments or examinations performed.
          </p>

          <h3>Consent to Obtain External Prescription History</h3>
          <p className="form4-instruction">
            I understand Digestive Health Partners of Texas, P.A. utilizes
            electronic prescribing technology and participates with
            SureScripts. I understand that my prescription history from
            multiple other unaffiliated medical providers, insurance
            companies, and pharmacy benefit managers may be viewable by my
            providers and staff here, and it may include prescriptions back
            in time for several years. Detailed prescription history
            provides your provider valuable information and improves
            accuracy in your medication list.
          </p>

          <h3>Electronic Communication</h3>
          <p className="form4-instruction">
            As a service to our patients, we provide courtesy appointment
            reminder calls and, when possible, text messages. We also may
            place other important calls and send text messages using a
            prerecorded or automated message.
          </p>

          <h3>Notice of Privacy Practices</h3>
          <p className="form4-instruction">
            I acknowledge that I have been given the opportunity to receive
            the Notice of Privacy Practices. This notice identifies how
            medical information about you may be used and disclosed, and how
            you can gain access to this information.
          </p>
          <p className="form4-instruction">
            I understand that the duration of this authorization is
            indefinite unless otherwise revoked in writing.
          </p>
          <div className="form4-row form4-row-2">
            {this.field("printName", "Print Name", { required: true, submitted })}
          </div>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">
            Patient Authorization for Personal Representative
          </h2>
          <p className="form4-instruction">
            I authorize Digestive Health Partners of Texas, PLLC. to disclose
            or provide my protected health information to the following
            individual(s) who is authorized to act as my personal
            representative for the purposes of receiving all protected
            health information about myself. As my designated personal
            representative, he/she may exercise my right to inspect, copy,
            and request amendments to my protected health information.
            He/she may also consent or authorize the use or disclosure of my
            protected health information.
          </p>
          <ul className="form4-static-list">
            <li>
              You authorize the practice to disclose all your protected
              health information to your designated personal representative.
            </li>
            <li>
              This authorization will remain in effect until terminated by
              you, your personal representative or another individual(s) of
              legal entity authorized to do so by court order or law.
            </li>
            <li>
              As stated in our Notice of Privacy Practices, you have the
              right to revoke or terminate this authorization by submitting
              a written request to our Privacy Manager. This can be done
              in-person or by mailing a request to: Dr Yamini Maddala/DHPT,
              Attn: Privacy Manager.
            </li>
          </ul>
          <label className="form4-inline-check">
            <input
              type="checkbox"
              checked={s.hasPersonalRepresentatives}
              onChange={() =>
                this.setState((prev) => ({
                  hasPersonalRepresentatives: !prev.hasPersonalRepresentatives,
                }))
              }
            />
            I would like to designate one or more personal representatives
          </label>
          {s.hasPersonalRepresentatives && (
            <div className="form4-subsection">
              {[1, 2, 3].map((n) => (
                <div className="form4-row form4-row-3" key={n}>
                  {this.field(`rep${n}Name`, `Name of Personal Representative ${n}`, { submitted })}
                  {this.field(`rep${n}Relationship`, "Relationship", { submitted })}
                  {this.field(`rep${n}Phone`, "Phone Number", {
                    submitted,
                    onChangeFn: this.onPhoneChange,
                    maxLength: "12",
                    placeholder: "000-000-0000",
                  })}
                </div>
              ))}
            </div>
          )}
          <p className="form4-instruction" style={{ marginTop: "10px" }}>
            <strong>Re-disclosure:</strong> We have no control over the
            person(s) you have listed as your personal representative.
            Therefore, your protected health information disclosed under
            this authorization will no longer be protected by requirements
            of the Privacy Rule and will no longer be the responsibility of
            this practice. Copies of signed authorizations are available
            upon request.
          </p>
        </section>

        <SignaturePad
          label="Patient Signature"
          name="signaturePage3"
          value={this.state.signaturePage3}
          onChange={this.onSignatureChange}
          error={submitted && !this.state.signaturePage3 ? "Signature is required" : ""}
        />
      </>
    );
  }

  // ============================================================
  // Step 4: Medical History
  // ============================================================
  renderTable(tableKey, columns, factory, denyKey, denyLabel) {
    const s = this.state;
    const denied = denyKey ? s[denyKey] : false;
    return (
      <div className="form4-table-block">
        {denyKey && (
          <label className="form4-inline-check form4-question-label">
            <input
              type="checkbox"
              checked={denied}
              onChange={() => this.setState({ [denyKey]: !denied })}
            />
            {denyLabel}
          </label>
        )}
        {!denied && (
          <>
            <table className="form4-dyn-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {s[tableKey].map((row, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c.key}>
                        <input
                          type="text"
                          value={row[c.key]}
                          onChange={(e) =>
                            this.updateRow(tableKey, i, c.key, e.target.value)
                          }
                        />
                      </td>
                    ))}
                    <td>
                      <span
                        className="form4-row-remove"
                        onClick={() => this.removeRow(tableKey, i)}
                      >
                        &times;
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <span
              className="form4-row-add"
              onClick={() => this.addRow(tableKey, factory)}
            >
              + Add row
            </span>
          </>
        )}
      </div>
    );
  }

  renderStep4() {
    const submitted = !!this.state.submittedSteps[4];
    const s = this.state;
    return (
      <>
        <section className="form4-section">
          <h2 className="form4-section-heading">Medical History</h2>
          {this.checkboxGroup("selectedConditions", MEDICAL_CONDITIONS_PLAIN)}

          <div className="form4-condition-detail">
            <label className="form4-inline-check">
              <input
                type="checkbox"
                checked={s.hasCancerCondition}
                onChange={() => this.setState({ hasCancerCondition: !s.hasCancerCondition })}
              />
              Cancer
            </label>
            {s.hasCancerCondition &&
              this.field("cancerType", "Cancer type", { className: "form4-field-lg" })}
          </div>

          <div className="form4-condition-detail">
            <label className="form4-inline-check">
              <input
                type="checkbox"
                checked={s.hasDiabetes}
                onChange={() => this.setState({ hasDiabetes: !s.hasDiabetes })}
              />
              Diabetes
            </label>
            {s.hasDiabetes && (
              <span className="form4-sub-options">
                <label>
                  <input
                    type="checkbox"
                    checked={s.diabetesType1}
                    onChange={() => this.setState({ diabetesType1: !s.diabetesType1 })}
                  />{" "}
                  Type I
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={s.diabetesType2}
                    onChange={() => this.setState({ diabetesType2: !s.diabetesType2 })}
                  />{" "}
                  Type II
                </label>
              </span>
            )}
          </div>

          <div className="form4-condition-detail">
            <label className="form4-inline-check">
              <input
                type="checkbox"
                checked={s.hasHepatitis}
                onChange={() => this.setState({ hasHepatitis: !s.hasHepatitis })}
              />
              Hepatitis
            </label>
            {s.hasHepatitis && (
              <span className="form4-sub-options">
                {["A", "B", "C"].map((t) => (
                  <label key={t}>
                    <input
                      type="checkbox"
                      checked={s[`hepatitis${t}`]}
                      onChange={() =>
                        this.setState({ [`hepatitis${t}`]: !s[`hepatitis${t}`] })
                      }
                    />{" "}
                    {t}
                  </label>
                ))}
              </span>
            )}
          </div>

          <div className="form4-condition-detail">
            <label className="form4-inline-check">
              <input
                type="checkbox"
                checked={s.hasHernia}
                onChange={() => this.setState({ hasHernia: !s.hasHernia })}
              />
              Hernia
            </label>
            {s.hasHernia && (
              <span className="form4-sub-options">
                <label>
                  <input
                    type="checkbox"
                    checked={s.herniaInguinal}
                    onChange={() => this.setState({ herniaInguinal: !s.herniaInguinal })}
                  />{" "}
                  Inguinal
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={s.herniaHiatal}
                    onChange={() => this.setState({ herniaHiatal: !s.herniaHiatal })}
                  />{" "}
                  Hiatal
                </label>
              </span>
            )}
          </div>

          <div className="form4-row form4-row-4" style={{ marginTop: "10px" }}>
            {this.field("otherCondition1", "Other")}
            {this.field("otherCondition2", "Other")}
            {this.field("otherCondition3", "Other")}
            {this.field("otherCondition4", "Other")}
          </div>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Medications</h2>
          {this.renderTable(
            "medications",
            [
              { key: "name", label: "Name" },
              { key: "dose", label: "Dose" },
              { key: "frequency", label: "Frequency" },
            ],
            emptyMedRow,
            "noMedications",
            "I am not currently taking any medications"
          )}
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Allergies</h2>
          {this.renderTable(
            "allergies",
            [
              { key: "medicationFood", label: "Medication/Food" },
              { key: "reaction", label: "Reaction" },
            ],
            emptyAllergyRow,
            "noKnownAllergies",
            "No Known Drug Allergies"
          )}
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Surgical History</h2>
          {this.renderTable(
            "surgicalHistory",
            [
              { key: "year", label: "Year" },
              { key: "detail", label: "Surgery" },
            ],
            emptyYearRow,
            "deniesSurgicalHistory",
            "Denies Past Surgical History"
          )}
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Hospitalizations</h2>
          {this.renderTable(
            "hospitalizations",
            [
              { key: "year", label: "Year" },
              { key: "detail", label: "Reason" },
            ],
            emptyYearRow,
            "deniesHospitalization",
            "Denies Past Hospitalization"
          )}
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Colonoscopy</h2>
          <label className="form4-inline-check form4-question-label">
            <input
              type="checkbox"
              checked={s.deniesPriorColonoscopy}
              onChange={() =>
                this.setState({ deniesPriorColonoscopy: !s.deniesPriorColonoscopy })
              }
            />
            Denies Prior Colonoscopy
          </label>
          {!s.deniesPriorColonoscopy && (
            <div className="form4-row form4-row-3">
              {this.field("lastColonoscopyDate", "When was your last colonoscopy?", { type: "date" })}
              {this.pillGroup("hadPolyps", "Did you have polyps", ["Yes", "No"], false, submitted)}
              {this.field("lastColonoscopyPerformedBy", "Who performed your last colonoscopy?")}
            </div>
          )}
        </section>

        <SignaturePad
          label="Patient Signature"
          name="signaturePage4"
          value={this.state.signaturePage4}
          onChange={this.onSignatureChange}
          error={submitted && !this.state.signaturePage4 ? "Signature is required" : ""}
        />
      </>
    );
  }

  // ============================================================
  // Step 5: Medical History continued
  // ============================================================
  renderStep5() {
    const submitted = !!this.state.submittedSteps[5];
    const s = this.state;
    return (
      <>
        <section className="form4-section">
          <h2 className="form4-section-heading">Family History</h2>
          <div className="form4-row form4-row-3">
            {this.pillGroup("fatherStatus", "Father", ["Alive", "Deceased"], false, submitted)}
            {this.field("fatherYearOfBirth", "Year of Birth", {
              type: "number",
              className: "form4-field-sm",
              min: "1900",
              max: String(new Date().getFullYear()),
            })}
            {this.field("fatherIllness", "Illness/Condition")}
          </div>
          <div className="form4-row form4-row-3">
            {this.pillGroup("motherStatus", "Mother", ["Alive", "Deceased"], false, submitted)}
            {this.field("motherYearOfBirth", "Year of Birth", {
              type: "number",
              className: "form4-field-sm",
              min: "1900",
              max: String(new Date().getFullYear()),
            })}
            {this.field("motherIllness", "Illness/Condition")}
          </div>

          <p className="form4-instruction">
            Is there any family history of the following? Please list the
            family member.
          </p>
          {FAMILY_HISTORY_ITEMS.map((item) => (
            <div className="form4-row form4-row-3" key={item.key}>
              <div className="form4-field">
                {this.yesNo(`fh_${item.key}_yn`, item.label)}
              </div>
              {item.hasType &&
                this.field(`fh_${item.key}_type`, "Type of Cancer")}
              {this.field(`fh_${item.key}_member`, "Family Member", {
                className: "form4-field-lg",
              })}
            </div>
          ))}

          <div className="form4-row form4-row-4">
            {this.field("numBrothers", "Number of Brothers")}
            {this.field("numSisters", "Number of Sisters")}
            {this.field("numSons", "Number of Sons")}
            {this.field("numDaughters", "Number of Daughters")}
          </div>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Social History</h2>
          {this.pillGroup(
            "tobaccoUse",
            "Tobacco Use",
            ["Current smoker", "Some day smoker", "Former smoker", "Nonsmoker"],
            false,
            submitted
          )}
          {(s.tobaccoUse === "Current smoker" || s.tobaccoUse === "Some day smoker") && (
            <>
              {this.pillGroup(
                "smokerFrequency",
                "If current smoker, how often do you smoke?",
                ["Everyday", "Some days, but not everyday"],
                false,
                submitted
              )}
              {this.pillGroup(
                "cigsPerDay",
                "How many do you smoke daily?",
                ["5 or less", "6-10", "11-20", "21-30", "More"],
                false,
                submitted
              )}
              {this.pillGroup(
                "howSoonAfterWaking",
                "How soon after you wake up do you smoke?",
                ["5 min or less", "6-30 min", "31-60 min", "after 60 min"],
                false,
                submitted
              )}
            </>
          )}

          {this.pillGroup(
            "alcoholFrequency",
            "How often did you drink alcohol in the past year?",
            ["Never", "Monthly or less", "2-4 times a month", "2-3 times a week", "4 or more times a week"],
            false,
            submitted
          )}
          {this.pillGroup(
            "drinksPerTypicalDay",
            "How many drinks on a typical day?",
            ["1-2", "3-4", "5-6", "7-9", "10 or more"],
            false,
            submitted
          )}
          {this.pillGroup(
            "sixPlusDrinksFrequency",
            "How often did you have 6 or more drinks at once?",
            ["Never", "Less than monthly", "Monthly", "Weekly", "Daily or almost daily"],
            false,
            submitted
          )}

          <div className="form4-row form4-row-2">
            <div className="form4-field">{this.yesNo("hasTattoos", "Do you have any tattoos?")}</div>
            <div className="form4-field">{this.yesNo("hasPiercings", "Do you have any piercings?")}</div>
          </div>

          {this.pillGroup(
            "illicitDrugUse",
            "Illicit Drug Use",
            ["None", "I used drugs in the past", "I currently use recreational drugs", "Treated for substance abuse"],
            false,
            submitted
          )}

          <div className="form4-row form4-row-3">
            <div className="form4-field">{this.yesNo("drinksCaffeine", "Do you drink caffeine?")}</div>
            {this.field("caffeineType", "If yes, what type?")}
            {this.field("caffeinePerDay", "How many per day?")}
          </div>
          <div className="form4-row form4-row-2">
            <div className="form4-field">
              {this.yesNo("hadBloodTransfusion", "Have you ever had a blood transfusion?")}
            </div>
            {this.field("bloodTransfusionYear", "If yes, what year?")}
          </div>
          <div className="form4-row form4-row-2">
            <div className="form4-field">
              {this.yesNo("traveledOutsideUS", "Have you recently traveled outside the US?")}
            </div>
            {this.field("travelDestination", "If yes, where?")}
          </div>
        </section>

        <section className="form4-section">
          <h2 className="form4-section-heading">Current Symptoms</h2>
          {Object.entries(SYMPTOM_GROUPS).map(([group, items]) => (
            <div className="form4-symptom-group" key={group}>
              <div className="form4-symptom-group-title">{group}</div>
              {this.checkboxGroup("selectedSymptoms", items)}
            </div>
          ))}
        </section>

        <SignaturePad
          label="Patient Signature"
          name="signaturePage5"
          value={this.state.signaturePage5}
          onChange={this.onSignatureChange}
          error={submitted && !this.state.signaturePage5 ? "Signature is required" : ""}
        />
      </>
    );
  }

  renderPasswordGate() {
    const { passwordInput, passwordError, passwordChecking, showPassword } = this.state;
    return (
      <div className="form4-main">
        <div className="form4-wrapper form4-password-wrapper">
          <h1 className="form4-title">New Patient Registration</h1>
          <p className="form4-required-legend">
            Please enter the access code to continue.
          </p>
          <form className="form4-password-form" onSubmit={this.onPasswordSubmit}>
            <div className="form4-field">
              <label>Access Code</label>
              <div className="form4-password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordInput}
                  onChange={this.onPasswordChange}
                  className={`form4-password-input ${passwordError ? "form4-input-error" : ""}`}
                  autoFocus
                />
                <button
                  type="button"
                  className="form4-password-eye"
                  onClick={this.toggleShowPassword}
                  aria-label={showPassword ? "Hide access code" : "Show access code"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.6 18.6 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.6 18.6 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && (
                <span className="form4-error">{passwordError}</span>
              )}
            </div>
            <button type="submit" className="form4-next-btn" disabled={passwordChecking}>
              {passwordChecking ? "Checking..." : "Continue"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  render() {
    const { step, errorMessage, loading, unlocked } = this.state;

    if (!unlocked) {
      return this.renderPasswordGate();
    }

    return (
      <div>
        <div className="form4-main">
          <div className="form4-wrapper">
            <h1 className="form4-title">New Patient Registration</h1>
            <p className="form4-required-legend">
              Fields marked with <span className="form4-dot">*</span> are
              required.
            </p>

            <div className="form4-progress">
              {STEP_TITLES.map((title, i) => {
                const stepNum = i + 1;
                const locked = stepNum > this.state.maxStepReached;
                return (
                  <div
                    key={title}
                    className={`form4-progress-step ${
                      step === stepNum ? "form4-progress-active" : ""
                    } ${step > stepNum ? "form4-progress-done" : ""} ${
                      locked ? "form4-progress-locked" : ""
                    }`}
                    onClick={() => this.goToProgressStep(stepNum)}
                    style={locked ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
                  >
                    <span className="form4-progress-number">{stepNum}</span>
                    <span className="form4-progress-label">{title}</span>
                  </div>
                );
              })}
            </div>

            {errorMessage && (
              <div className="alert">
                <span className="closebtn" onClick={this.handleAlert}>
                  &times;
                </span>
                {errorMessage}
              </div>
            )}

            <form
              className="form4-form"
              noValidate
              onSubmit={(e) => e.preventDefault()}
            >
              {step === 1 && this.renderStep1()}
              {step === 2 && this.renderStep2()}
              {step === 3 && this.renderStep3()}
              {step === 4 && this.renderStep4()}
              {step === 5 && this.renderStep5()}

              <div className="form4-nav">
                {step > 1 && (
                  <button
                    type="button"
                    className="form4-back-btn"
                    onClick={this.goBack}
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  className="form4-next-btn"
                  onClick={this.goNext}
                >
                  {step < 5 ? "Sign & Continue" : "Sign & Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {loading && (
          <div className="loader-container">
            <div className="loader-content">
              <div className="loader"></div>
              <span className="loader-text">Submitting...</span>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(Form4);
